import { z } from 'zod';
import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  PORTAL_ERROR_MESSAGES,
  PORTAL_REPORT_LIMITS,
  portalErrorKind,
  portalErrorReportSchema,
  type PortalErrorKind,
  type PortalErrorReport,
} from '@erp/shared';
import { toast } from 'sonner';
import { ApiError } from './api-client';
import { i18n } from './i18n';

const STORAGE = 'erp-portal-diagnostics-v1';
const IDENTITY = 'erp-portal-diagnostic-actor';
const queueEntrySchema = z.object({
  event: portalErrorReportSchema,
  attempts: z.number().int().min(0),
  expires: z.number(),
});
type QueueEntry = z.infer<typeof queueEntrySchema>;
type RequestContext = {
  attemptId: string;
  action: string;
  correlationId: string | null;
  targetId: string | null;
};
const errors = new WeakMap<object, RequestContext>();
const reported = new WeakMap<object, Set<string>>();
const toasted = new WeakSet<object>();
const reportedPrimitives = new Set<string>();
let flushing = false;
let queue: QueueEntry[] = [];
try {
  queue = z
    .array(queueEntrySchema)
    .parse(JSON.parse(sessionStorage.getItem(STORAGE) ?? '[]'))
    .slice(-PORTAL_REPORT_LIMITS.queue);
} catch {
  queue = [];
}

function persist(): void {
  try {
    sessionStorage.setItem(STORAGE, JSON.stringify(queue));
  } catch {
    /* In-memory retry remains available when storage is blocked or full. */
  }
}
export function setPortalDiagnosticActor(id: string | null): void {
  try {
    if (id === null) sessionStorage.removeItem(IDENTITY);
    else sessionStorage.setItem(IDENTITY, id);
  } catch {
    /* Authenticated server events still provide independent evidence. */
  }
  void flushPortalDiagnostics();
}
export function portalDiagnosticActor(): string | null {
  // Public forms cannot inherit the identity of a previous session in this tab.
  if (/^\/portal\/(?:login|accept|reset)\/?$/u.test(window.location.pathname)) return null;
  try {
    return sessionStorage.getItem(IDENTITY);
  } catch {
    return null;
  }
}
export function associatePortalError(error: unknown, context: RequestContext): void {
  if (typeof error === 'object' && error !== null) errors.set(error, context);
}
export function portalErrorPresentation(error: unknown, kind?: PortalErrorKind) {
  const code =
    error instanceof ApiError
      ? error.code
      : error instanceof TypeError
        ? 'NETWORK_ERROR'
        : kind === 'price'
          ? 'PORTAL_PRICE_CHANGED'
          : kind === 'validation'
            ? 'VALIDATION_FAILED'
            : 'CLIENT_ERROR';
  const messageKey =
    kind ?? portalErrorKind(code, error instanceof ApiError ? error.status : undefined);
  const locale = isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;
  return {
    code,
    messageKey,
    locale,
    message: PORTAL_ERROR_MESSAGES[locale][messageKey],
    reference: error instanceof ApiError ? (error.trackingId ?? error.traceId) : undefined,
  };
}
export function reportPortalError(
  error: unknown,
  kind?: PortalErrorKind,
  action = 'view',
  targetId: string | null = null,
  count?: number,
  correlationId?: string,
): void {
  if (!window.location.pathname.startsWith('/portal')) return;
  const presentation = portalErrorPresentation(error, kind);
  const object = typeof error === 'object' && error !== null ? error : null;
  if (object !== null) {
    const seen = reported.get(object) ?? new Set<string>();
    const key = `${presentation.locale}:${presentation.messageKey}`;
    if (seen.has(key)) return;
    seen.add(key);
    reported.set(object, seen);
  }
  if (object === null) {
    const key = `${window.location.pathname}:${String(error)}:${presentation.locale}:${presentation.messageKey}`;
    if (reportedPrimitives.has(key)) return;
    reportedPrimitives.add(key);
    if (reportedPrimitives.size > PORTAL_REPORT_LIMITS.queue)
      reportedPrimitives.delete(reportedPrimitives.values().next().value ?? '');
  }
  const context = object === null ? undefined : errors.get(object);
  const event: PortalErrorReport = {
    eventId: crypto.randomUUID(),
    actorId: portalDiagnosticActor(),
    attemptId: context?.attemptId ?? crypto.randomUUID(),
    action: context?.action ?? action,
    correlationId: context?.correlationId ?? correlationId ?? null,
    targetId: targetId ?? context?.targetId ?? null,
    page: window.location.pathname.replace(/\/$/u, '') || '/portal',
    requestId: error instanceof ApiError ? (error.traceId ?? null) : null,
    trackingId: error instanceof ApiError ? (error.trackingId ?? null) : null,
    code: presentation.code,
    messageKey: presentation.messageKey,
    locale: presentation.locale,
    parameters: count === undefined ? {} : { count },
    occurredAt: new Date().toISOString(),
  };
  const parsed = portalErrorReportSchema.safeParse(event);
  if (!parsed.success) return;
  queue.push({
    event: parsed.data,
    attempts: 0,
    expires: Date.now() + PORTAL_REPORT_LIMITS.maxAgeMs,
  });
  queue = queue.slice(-PORTAL_REPORT_LIMITS.queue);
  persist();
  void flushPortalDiagnostics();
}
export function portalToastError(error: unknown, kind?: PortalErrorKind): void {
  if (typeof error === 'object' && error !== null) {
    if (toasted.has(error)) return;
    toasted.add(error);
  }
  const presentation = portalErrorPresentation(error, kind);
  reportPortalError(error, kind);
  toast.error(presentation.message, { description: presentation.reference });
}
export async function flushPortalDiagnostics(): Promise<void> {
  if (flushing || !navigator.onLine) return;
  queue = queue.filter(
    (entry) => entry.expires > Date.now() && entry.attempts < PORTAL_REPORT_LIMITS.attempts,
  );
  const actorId = portalDiagnosticActor();
  const batch = queue
    .filter((entry) => entry.event.actorId === actorId)
    .slice(0, PORTAL_REPORT_LIMITS.batch);
  if (batch.length === 0) {
    persist();
    return;
  }
  flushing = true;
  batch.forEach((entry) => entry.attempts++);
  persist();
  try {
    const csrf = sessionStorage.getItem('erp-portal-csrf');
    const response = await fetch(
      `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1/portal/diagnostics`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          ...(csrf === null ? {} : { 'x-portal-csrf': csrf }),
        },
        body: JSON.stringify({ events: batch.map((entry) => entry.event) }),
        signal: AbortSignal.timeout(5000),
      },
    );
    if (response.ok) {
      const ids = new Set(batch.map((entry) => entry.event.eventId));
      queue = queue.filter((entry) => !ids.has(entry.event.eventId));
    }
  } catch {
    /* Diagnostics never invoke business requests, refresh, or their own error reporter. */
  } finally {
    flushing = false;
    persist();
  }
}

window.addEventListener('online', () => void flushPortalDiagnostics());
window.setInterval(() => void flushPortalDiagnostics(), PORTAL_REPORT_LIMITS.retryMs);
let validationScheduled = false;
document.addEventListener(
  'invalid',
  (event) => {
    if (!location.pathname.startsWith('/portal')) return;
    event.preventDefault();
    if (validationScheduled) return;
    if (event.target instanceof HTMLElement) event.target.focus();
    validationScheduled = true;
    queueMicrotask(() => {
      validationScheduled = false;
      const error = new Error('validation');
      reportPortalError(error, 'validation', 'validate form');
      portalToastError(error, 'validation');
    });
  },
  true,
);
window.addEventListener('error', () => {
  if (location.pathname.startsWith('/portal')) portalToastError(new Error('client'), 'server');
});
window.addEventListener('unhandledrejection', () => {
  if (location.pathname.startsWith('/portal')) portalToastError(new Error('client'), 'server');
});
